// Predictive search (<predictive-search>), progressive enhancement over a
// plain GET form: without this script the form still submits to /search.
// Follows the official UX guidelines: results appear while typing, the
// dropdown disappears when there is nothing to show, the query is never
// cleared by us, and Enter always reaches the results page.

const DEBOUNCE_MS = 300;

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-search-input]');
    this.results = this.querySelector('[data-predictive-results]');
    this.status = this.querySelector('[data-search-status]');
    this.endpoint = this.dataset.url;
    if (!this.input || !this.results || !this.endpoint) return;

    this.selected = -1;

    this.handleInput = () => {
      clearTimeout(this.timer);
      const term = this.input.value.trim();
      if (term.length === 0) {
        this.clear();
        return;
      }
      this.timer = setTimeout(() => this.search(term), DEBOUNCE_MS);
    };

    this.handleKeydown = (event) => {
      const options = this.options();
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (options.length === 0) return;
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        this.select((this.selected + step + options.length) % options.length);
        return;
      }
      if (event.key === 'Enter' && this.selected > -1) {
        const link = this.options()[this.selected]?.querySelector('a');
        if (link) {
          event.preventDefault();
          link.click();
        }
        return;
      }
      if (event.key === 'Escape' && this.results.innerHTML !== '') {
        event.stopPropagation();
        this.clear();
      }
    };

    this.input.addEventListener('input', this.handleInput);
    this.input.addEventListener('keydown', this.handleKeydown);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.controller?.abort();
    this.input?.removeEventListener('input', this.handleInput);
    this.input?.removeEventListener('keydown', this.handleKeydown);
  }

  options() {
    return Array.from(this.results.querySelectorAll('[role="option"]'));
  }

  select(index) {
    const options = this.options();
    options.forEach((option, i) => option.setAttribute('aria-selected', String(i === index)));
    this.selected = index;
    const current = options[index];
    if (current) {
      this.input.setAttribute('aria-activedescendant', current.id);
      current.scrollIntoView({ block: 'nearest' });
    }
  }

  clear() {
    this.controller?.abort();
    this.results.innerHTML = '';
    this.selected = -1;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    if (this.status) this.status.textContent = '';
  }

  async search(term) {
    this.controller?.abort();
    this.controller = new AbortController();

    const url = `${this.endpoint}?q=${encodeURIComponent(term)}&resources[type]=query,product,collection,page,article&resources[limit]=4&section_id=predictive-search`;

    try {
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) throw new Error(response.statusText);
      const text = await response.text();
      const markup = new DOMParser().parseFromString(text, 'text/html').querySelector('#predictive-search-list');

      this.selected = -1;
      this.input.removeAttribute('aria-activedescendant');
      this.results.innerHTML = markup ? markup.outerHTML : '';
      this.input.setAttribute('aria-expanded', markup ? 'true' : 'false');

      // Announce how many suggestions arrived; the count text is rendered
      // (and translated) by the section, so this stays Liquid-free.
      if (this.status) {
        this.status.textContent = markup?.querySelector('[data-results-count]')?.textContent.trim() || '';
      }
    } catch (error) {
      if (error.name !== 'AbortError') this.clear();
    }
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
